import { useEffect, useMemo } from "react";
import { Select } from "./ui";
import type { RateOption } from "@/lib/types";

export interface ScheduleSelection {
  collectionDate: string;
  collectionAfter: string;
  collectionBefore: string;
  deliveryAfter: string;
  deliveryBefore: string;
}

export const EMPTY_SCHEDULE: ScheduleSelection = {
  collectionDate: "",
  collectionAfter: "",
  collectionBefore: "",
  deliveryAfter: "",
  deliveryBefore: "",
};

const SLOT_MINUTES = 30;
const DAYS_OFFERED = 14;

function parseClock(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/** Half-hour slots across a window, inclusive of both ends. */
function slots(start: string, end: string): string[] {
  const from = parseClock(start) ?? 6 * 60;
  const to = parseClock(end) ?? 20 * 60;
  const out: string[] = [];
  for (let m = from; m <= to; m += SLOT_MINUTES) out.push(clock(m));
  return out;
}

function isWeekend(date: Date): boolean {
  return date.getDay() === 0 || date.getDay() === 6;
}

function toISODate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Working days from the earliest this option can collect. */
function collectionDates(earliest: string | undefined): string[] {
  const start = earliest ? new Date(`${earliest}T00:00:00`) : new Date();
  if (Number.isNaN(start.getTime())) return [];

  const out: string[] = [];
  const day = new Date(start);
  while (out.length < DAYS_OFFERED) {
    if (!isWeekend(day)) out.push(toISODate(day));
    day.setDate(day.getDate() + 1);
  }
  return out;
}

export function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;

  const today = toISODate(new Date());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const label = date.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" });
  if (iso === today) return `Today, ${label}`;
  if (iso === toISODate(tomorrow)) return `Tomorrow, ${label}`;
  return label;
}

/** "Wed, 23 Sep" or "Wed, 23 Sep - Thu, 24 Sep" for a delivery range. */
export function formatDeliveryEstimate(option: RateOption): string {
  const from = option.expected_delivery_from;
  const to = option.expected_delivery_to;
  if (!from && !to) return "";
  if (!from || !to || from === to) return formatDate((from ?? to) as string);
  return `${formatDate(from)} - ${formatDate(to)}`;
}

/**
 * When a shipment is collected and delivered, picked rather than typed.
 *
 * Every choice offered here comes from the service level the customer
 * actually selected: its earliest collection day, and the hours it is
 * rostered for. A free-text time field let someone ask for a 03:00
 * collection that the booking would then refuse -- the dropdown cannot.
 */
export function SchedulePicker({
  option,
  value,
  onChange,
  errors = {},
}: {
  option: RateOption;
  value: ScheduleSelection;
  onChange: (next: ScheduleSelection) => void;
  errors?: Record<string, string>;
}) {
  const dates = useMemo(() => collectionDates(option.earliest_collection_date), [option.earliest_collection_date]);
  const collectionSlots = useMemo(
    () => slots(option.collection_window_start ?? "", option.collection_window_end ?? ""),
    [option.collection_window_start, option.collection_window_end],
  );
  const deliverySlots = useMemo(
    () => slots(option.delivery_window_start ?? "", option.delivery_window_end ?? ""),
    [option.delivery_window_start, option.delivery_window_end],
  );

  // Default to the whole of each window on the first working day the option
  // allows, and re-default whenever the customer switches option: a time that
  // was inside yesterday's choice may be outside this one's.
  useEffect(() => {
    onChange({
      collectionDate: dates[0] ?? "",
      collectionAfter: collectionSlots[0] ?? "",
      collectionBefore: collectionSlots[collectionSlots.length - 1] ?? "",
      deliveryAfter: deliverySlots[0] ?? "",
      deliveryBefore: deliverySlots[deliverySlots.length - 1] ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option.id]);

  const set = (patch: Partial<ScheduleSelection>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      <div>
        <p className="ws-label">Collection</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            label="Date"
            name="collection-date"
            value={value.collectionDate}
            onChange={(e) => set({ collectionDate: e.target.value })}
            error={errors.collection_date}
            options={dates.map((date) => ({ value: date, label: formatDate(date) }))}
          />
          <Select
            label="From"
            name="collection-after"
            value={value.collectionAfter}
            onChange={(e) => set({ collectionAfter: e.target.value })}
            error={errors.requested_collection_after}
            options={collectionSlots.map((t) => ({ value: t, label: t }))}
          />
          <Select
            label="Until"
            name="collection-before"
            value={value.collectionBefore}
            onChange={(e) => set({ collectionBefore: e.target.value })}
            error={errors.requested_collection_before}
            options={collectionSlots.map((t) => ({ value: t, label: t }))}
          />
        </div>
      </div>

      <div>
        <p className="ws-label">Delivery</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-ink-100 px-3 py-2.5 text-sm">
            <span className="block text-xs text-ink-500">Expected</span>
            {formatDeliveryEstimate(option) || "Confirmed after collection"}
          </div>
          <Select
            label="From"
            name="delivery-after"
            value={value.deliveryAfter}
            onChange={(e) => set({ deliveryAfter: e.target.value })}
            error={errors.requested_delivery_after}
            options={deliverySlots.map((t) => ({ value: t, label: t }))}
          />
          <Select
            label="Until"
            name="delivery-before"
            value={value.deliveryBefore}
            onChange={(e) => set({ deliveryBefore: e.target.value })}
            error={errors.requested_delivery_before}
            options={deliverySlots.map((t) => ({ value: t, label: t }))}
          />
        </div>
        <p className="ws-hint">
          A preference passed to the driver, not a guarantee. The delivery date is set by the service you chose.
        </p>
      </div>
    </div>
  );
}
